if (!customElements.get("shipping-calculator")) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector("button");
      this.button.addEventListener("click", this.handleSubmit.bind(this));
      this.init();
    }

    init() {
      this.country = this.querySelector(".js-country-select");
      this.province = this.querySelector(".js-province-select");
      this.zip = this.querySelector(".js-zip-input");
      this.rates = this.querySelector(".js-rates");
      this.errors = this.querySelector(".js-errors");
      this.initialised = true;
      this.setupZipValidation();
    }

    setupZipValidation() {
      if (this.zip) {
        this.zip.addEventListener('input', this.validateZip.bind(this));
        this.zip.addEventListener('keypress', this.allowOnlyNumbers.bind(this));
        this.validateZip();
      }
    }

    allowOnlyNumbers(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    }

    validateZip() {
      const zipValue = this.zip.value.replace(/\D/g, '');
      
      if (zipValue.length >= 8) {
        this.button.disabled = false;
        this.button.classList.remove('disabled');
        this.button.classList.add('enabled');
      } else {
        this.button.disabled = true;
        this.button.classList.add('disabled');
        this.button.classList.remove('enabled');
      }
    }

    /**
     * Handles submission of the rates calculator.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      if (!this.initialised) this.init();

      const zipValue = this.zip.value.replace(/\D/g, '');
      if (zipValue.length < 8) {
        this.showErrors({
          zip: "Por favor, digite um CEP válido com 8 dígitos."
        });
        return;
      }

      this.errors.hidden = true;
      this.rates.hidden = true;
      this.button.disabled = true;
      this.button.querySelector(".loading__spinner").classList.remove("hidden");
      this.button.querySelector(".button-text").classList.add("hidden");

      const params = `shipping_address[zip]=${this.zip.value}&shipping_address[country]=${this.country.value}&shipping_address[province]=${this.province.value}`;

      try {
        const cartResponse = await fetch(`${theme.routes.cart || '/cart'}.js`);
        const cartData = await cartResponse.json();

        if (window.ShopifyInfo) {
          window.ShopifyInfo.cartTotal = cartData.total_price;
        }
  
        const response = await fetch(
          `${theme.routes.cart}/shipping_rates.json?${params}`
        );
        const data = await response.json();

        if (response.ok) {
          this.showRates(data);
        } else {
          this.showErrors(data);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        this.button.querySelector(".loading__spinner").classList.add("hidden");
        this.button.querySelector(".button-text").classList.remove("hidden");
        this.validateZip();
      }
    }

    /**
     * Formats and shows a list of available shipping rates.
     * @param {object} data - Response data.
     */
    showRates(data) {
      const headingEl = this.rates.querySelector(".js-rates-heading");
      const textEl = this.rates.querySelector(".js-rates-text");
      textEl.innerHTML = "";

      const metaFreeShipping = window.ShopifyInfo?.freeShippingGoal ?? Infinity;
      const valorTotalCarrinho = window.ShopifyInfo?.cartTotal || 0;
      const coverage = window.ShopifyInfo?.freeShippingCoverage || 'all';
      const eligiblePrefixes = window.ShopifyInfo?.eligiblePrefixes || [];
      const eligibleZips = window.ShopifyInfo?.eligibleZips || [];
      const comboFree = document.getElementById("free-shipping-bar")?.dataset.comboFree === "true";

      let isEligible = false;
      const currentZip = this.zip ? this.zip.value.replace(/\D/g, '') : "";
      const zipPrefix2 = currentZip.substring(0, 2);

      if (coverage === 'all') {
        isEligible = true;
      } else if (coverage === 'regions') {
        isEligible = eligiblePrefixes.includes(zipPrefix2);
      } else if (coverage === 'zips') {
        isEligible = eligibleZips.some(prefix => prefix && currentZip.startsWith(prefix));
      }

      if (data.shipping_rates && data.shipping_rates.length) {
        const headingLocaleStr =
          data.shipping_rates.length === 1 ? "singleRate" : "multipleRates";
        let rates = "";

        data.shipping_rates.forEach((rate) => {
          let deliveryDays = ""
          if (rate.delivery_days && rate.delivery_days.length > 0 && rate.delivery_days[0] !== undefined && rate.delivery_days[1] !== undefined) {
            if(rate.delivery_days[0] == rate.delivery_days[1]) {
              deliveryDays = `${rate.delivery_days[0]} dias úteis`;
            } else {
              deliveryDays = `${rate.delivery_days[0]} a ${rate.delivery_days[1]} dias úteis`;
            }
          }
          
          let formattedRate = "";
          
          let precoFormatadoBase = rate.currency === "BRL"
              ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(rate.price)
              : theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, rate.price);

          if (rate.price === "0.00") {
            formattedRate = "<strong>GRÁTIS</strong>";
          } else if (comboFree || (valorTotalCarrinho >= metaFreeShipping && isEligible)) {
            formattedRate = `<span style="text-decoration: line-through; opacity: 0.6; margin-right: 5px;">${precoFormatadoBase}</span> <strong style="color: #2e8b57;">GRÁTIS</strong>`;
          } else {
            formattedRate = precoFormatadoBase;
          }

          rates += `<li>
                      <span class="rate-name">${rate.name}:</span>
                      <span class="formatted-rate">${formattedRate}</span>
                      <span class="delivery-days">${deliveryDays}</span>
                    </li>`;
        });

        headingEl.hidden = false;
        headingEl.textContent =
          theme.strings.shippingCalculator[headingLocaleStr];
        textEl.innerHTML = `<ul class="styled-list">${rates}</ul>`;
      } else {
        headingEl.hidden = true;
        textEl.innerHTML = theme.strings.shippingCalculator.noRates;
      }

      this.rates.hidden = false;
    }

    /**
     * Formats and shows errors relating to rates retrieval.
     * @param {object} data - Response data.
     */
    showErrors(data) {
      let errors = "";

      Object.keys(data).forEach((key) => {
        errors += `<li>${data[key]}</li>`;
      });

      this.errors.querySelector(
        ".js-errors-text"
      ).innerHTML = `<ul class="styled-list">${errors}</ul>`;
      this.errors.hidden = false;
    }
  }

  customElements.define("shipping-calculator", ShippingCalculator);
}
